import {
  Button,
  Flex,
  Grid,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { useEffect, useState } from "react";
import LocalizedStrings from "react-localization";

/**
 * StockBioInputEntryForm - formulario para StockEntry_Bioinput (Ingresar Bioinsumo)
 * - No hay validaciones complejas ni uploads reales (photoEvidence como CSV/nombres)
 * - Selects con 5 opciones ficticias para referencias
 *
 * Props:
 * - onSubmit(formValues)
 * - type: "create" | "update"
 * - initialValues = {}
 * - onCancel() optional
 */

const FAKE_WAREHOUSES = [
  { id: "wh-0001", name: "Finca Las Palmas" },
  { id: "wh-0002", name: "Finca El Roble" },
  { id: "wh-0003", name: "Finca La Esperanza" },
  { id: "wh-0004", name: "Finca San José" },
  { id: "wh-0005", name: "Finca Santa Rita" },
];

const FAKE_COLLABORATORS = [
  { id: "col-0001", name: "Juan Pérez" },
  { id: "col-0002", name: "María Gómez" },
  { id: "col-0003", name: "Carlos Rojas" },
  { id: "col-0004", name: "Ana Martínez" },
  { id: "col-0005", name: "Luis Díaz" },
];

const FAKE_PRESCRIPTIONS = [
  { id: "rx-0001", name: "Receta A - Biofungicida" },
  { id: "rx-0002", name: "Receta B - Insecticida biológico" },
  { id: "rx-0003", name: "Receta C - Extracto vegetal" },
  { id: "rx-0004", name: "Receta D - Microbial mix" },
  { id: "rx-0005", name: "Receta E - Control natural" },
];

const FAKE_INPUTS = [
  { id: "in-0001", name: "Extracto de Neem" },
  { id: "in-0002", name: "Bacillus thuringiensis (BT)" },
  { id: "in-0003", name: "Trichoderma harzianum" },
  { id: "in-0004", name: "Levadura fermentada" },
  { id: "in-0005", name: "Extracto de Ajo" },
];

const FAKE_SHELVES = [
  { id: "shelf-01", name: "Estante A" },
  { id: "shelf-02", name: "Estante B" },
  { id: "shelf-03", name: "Estante C" },
  { id: "shelf-04", name: "Estante D" },
  { id: "shelf-05", name: "Estante E" },
];

const FAKE_LEVELS = ["", "1", "2", "3", "4"];

const APPLICATION_UNITS = ["", "Liter", "CropUnit"];

const StockBioInputEntryForm = ({
  onSubmit,
  type = "create",
  initialValues = {},
  onCancel,
}) => {
  const defaultState = {
    date: "",
    warehouseId: "",
    warehouseName: "",
    performedById: "",
    prescriptionId: "",
    prescriptionName: "",
    prescribingAgronomistId: "",
    preparationUnits: "",
    recipeDetails: "",
    inputId: "",
    inputName: "",
    availableInventoryQuantity: "",
    inputInventoryUnit: "",
    inputQuantityToUse: "",
    applicationUnit: "",
    preparationProcedure: "",
    photoEvidence: "",
    expirationDate: "",
    storageLocationWarehouseId: "",
    storageLocationWarehouseName: "",
    storageShelf: "",
    storageLevel: "",
    additionalObservations: "",
    createdAt: "",
  };

  const [form, setForm] = useState({ ...defaultState, ...initialValues });

  useEffect(() => {
    setForm({ ...defaultState, ...initialValues });
    // try to enrich a few fields from fake lists
    if (initialValues.warehouseId && !initialValues.warehouseName) {
      const found = FAKE_WAREHOUSES.find(
        (w) => w.id === initialValues.warehouseId
      );
      if (found) setForm((f) => ({ ...f, warehouseName: found.name }));
    }
    if (initialValues.prescriptionId && !initialValues.prescriptionName) {
      const found = FAKE_PRESCRIPTIONS.find(
        (p) => p.id === initialValues.prescriptionId
      );
      if (found) setForm((f) => ({ ...f, prescriptionName: found.name }));
    }
    if (initialValues.inputId && !initialValues.inputName) {
      const found = FAKE_INPUTS.find((i) => i.id === initialValues.inputId);
      if (found) setForm((f) => ({ ...f, inputName: found.name }));
    }
    if (
      initialValues.storageLocationWarehouseId &&
      !initialValues.storageLocationWarehouseName
    ) {
      const found = FAKE_WAREHOUSES.find(
        (w) => w.id === initialValues.storageLocationWarehouseId
      );
      if (found)
        setForm((f) => ({ ...f, storageLocationWarehouseName: found.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const onSubmitForm = () => {
    const payload = { ...form };
    if (!payload.createdAt)
      payload.createdAt = new Date().toISOString().slice(0, 10);
    onSubmit && onSubmit(payload);
  };

  return (
    <Grid rowGap="12px" columnGap="12px" padding="20px">
      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="date"
          label={strings.fields.date.label}
          placeholder="YYYY-MM-DD"
          value={form.date || ""}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
        />
        <SelectField
          name="warehouseId"
          label={strings.fields.warehouseId.label}
          value={form.warehouseId || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_WAREHOUSES.find((w) => w.id === val);
            setForm({
              ...form,
              warehouseId: val,
              warehouseName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_WAREHOUSES.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="performedById"
          label={strings.fields.performedById.label}
          value={form.performedById || ""}
          onChange={(e) => setForm({ ...form, performedById: e.target.value })}
        >
          <option value="">--</option>
          {FAKE_COLLABORATORS.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="prescriptionId"
          label={strings.fields.prescriptionId.label}
          value={form.prescriptionId || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_PRESCRIPTIONS.find((p) => p.id === val);
            setForm({
              ...form,
              prescriptionId: val,
              prescriptionName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_PRESCRIPTIONS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="prescribingAgronomistId"
          label={strings.fields.prescribingAgronomistId.label}
          value={form.prescribingAgronomistId || ""}
          onChange={(e) =>
            setForm({ ...form, prescribingAgronomistId: e.target.value })
          }
        />
        <TextField
          name="preparationUnits"
          label={strings.fields.preparationUnits.label}
          value={form.preparationUnits || ""}
          onChange={(e) =>
            setForm({ ...form, preparationUnits: e.target.value })
          }
        />
      </Grid>

      <TextAreaField
        name="recipeDetails"
        label={strings.fields.recipeDetails.label}
        value={form.recipeDetails || ""}
        onChange={(e) => setForm({ ...form, recipeDetails: e.target.value })}
      />

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr" }}>
        <SelectField
          name="inputId"
          label={strings.fields.inputId.label}
          value={form.inputId || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_INPUTS.find((i) => i.id === val);
            setForm({
              ...form,
              inputId: val,
              inputName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_INPUTS.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="availableInventoryQuantity"
          label={strings.fields.availableInventoryQuantity.label}
          value={form.availableInventoryQuantity || ""}
          onChange={(e) =>
            setForm({ ...form, availableInventoryQuantity: e.target.value })
          }
        />
        <TextField
          name="inputInventoryUnit"
          label={strings.fields.inputInventoryUnit.label}
          value={form.inputInventoryUnit || ""}
          onChange={(e) =>
            setForm({ ...form, inputInventoryUnit: e.target.value })
          }
        />
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="inputQuantityToUse"
          label={strings.fields.inputQuantityToUse.label}
          value={form.inputQuantityToUse || ""}
          onChange={(e) =>
            setForm({ ...form, inputQuantityToUse: e.target.value })
          }
        />
        <SelectField
          name="applicationUnit"
          label={strings.fields.applicationUnit.label}
          value={form.applicationUnit || ""}
          onChange={(e) =>
            setForm({ ...form, applicationUnit: e.target.value })
          }
        >
          {APPLICATION_UNITS.map((a) => (
            <option key={a} value={a}>
              {a || "--"}
            </option>
          ))}
        </SelectField>
      </Grid>

      <TextAreaField
        name="preparationProcedure"
        label={strings.fields.preparationProcedure.label}
        value={form.preparationProcedure || ""}
        onChange={(e) =>
          setForm({ ...form, preparationProcedure: e.target.value })
        }
      />

      <TextField
        name="photoEvidence"
        label={strings.fields.photoEvidence.label}
        placeholder="foto1.jpg, foto2.jpg"
        value={
          Array.isArray(form.photoEvidence)
            ? form.photoEvidence.join(", ")
            : form.photoEvidence || ""
        }
        onChange={(e) => setForm({ ...form, photoEvidence: e.target.value })}
      />

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <TextField
          name="expirationDate"
          label={strings.fields.expirationDate.label}
          placeholder="YYYY-MM-DD"
          value={form.expirationDate || ""}
          onChange={(e) => setForm({ ...form, expirationDate: e.target.value })}
        />
        <SelectField
          name="storageLocationWarehouseId"
          label={strings.fields.storageLocationWarehouseId.label}
          value={form.storageLocationWarehouseId || ""}
          onChange={(e) => {
            const val = e.target.value;
            const found = FAKE_WAREHOUSES.find((w) => w.id === val);
            setForm({
              ...form,
              storageLocationWarehouseId: val,
              storageLocationWarehouseName: found ? found.name : "",
            });
          }}
        >
          <option value="">--</option>
          {FAKE_WAREHOUSES.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </SelectField>
      </Grid>

      <Grid gap="12px" templateColumns={{ base: "1fr", large: "1fr 1fr" }}>
        <SelectField
          name="storageShelf"
          label={strings.fields.storageShelf.label}
          value={form.storageShelf || ""}
          onChange={(e) => setForm({ ...form, storageShelf: e.target.value })}
        >
          <option value="">--</option>
          {FAKE_SHELVES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>

        <SelectField
          name="storageLevel"
          label={strings.fields.storageLevel.label}
          value={form.storageLevel || ""}
          onChange={(e) => setForm({ ...form, storageLevel: e.target.value })}
        >
          {FAKE_LEVELS.map((l) => (
            <option key={l} value={l}>
              {l || "--"}
            </option>
          ))}
        </SelectField>
      </Grid>

      <TextAreaField
        name="additionalObservations"
        label={strings.fields.additionalObservations.label}
        value={form.additionalObservations || ""}
        onChange={(e) =>
          setForm({ ...form, additionalObservations: e.target.value })
        }
      />

      <Flex justifyContent="end">
        <Button
          children={strings.fields.buttons.reset}
          onClick={() => setForm(defaultState)}
        />
        <Button
          children={
            type === "create"
              ? strings.fields.buttons.create
              : strings.fields.buttons.save
          }
          onClick={onSubmitForm}
          variation="primary"
        />
      </Flex>
    </Grid>
  );
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      date: { label: "Fecha" },
      warehouseId: { label: "Finca / Almacén" },
      performedById: { label: "Persona responsable" },
      prescriptionId: { label: "Receta" },
      prescriptionName: { label: "Receta" },
      prescribingAgronomistId: { label: "Agrónomo que receta" },
      preparationUnits: { label: "Unidades de preparación (L / Kg)" },
      recipeDetails: { label: "Detalle receta" },
      inputId: { label: "Insumo utilizado" },
      inputName: { label: "Nombre Insumo" },
      availableInventoryQuantity: { label: "Cantidad disponible (inventario)" },
      inputInventoryUnit: { label: "Unidad de inventario" },
      inputQuantityToUse: { label: "Cantidad de insumo a usar (g / mL)" },
      applicationUnit: { label: "Unidad de aplicación" },
      preparationProcedure: { label: "Procedimiento de preparación" },
      photoEvidence: { label: "Evidencia fotográfica" },
      expirationDate: { label: "Fecha de vencimiento" },
      storageLocationWarehouseId: { label: "Almacén (almacenamiento)" },
      storageShelf: { label: "Estante (almacenamiento)" },
      storageLevel: { label: "Nivel (almacenamiento)" },
      additionalObservations: { label: "Observaciones adicionales" },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
        cancel: "Cancelar",
      },
    },
  },
});

export default StockBioInputEntryForm;
